import {
  GEM_KINDS,
  WELL_COLS,
  idx,
  idxCol,
  idxRow,
  type CellIndex,
  type GemKind,
  type Grid,
} from './types';
import { applyGravity, clearCells } from './grid';
import { findFullRows, findRuns, type MatchRun } from './match';
import {
  CASCADE_CAP,
  proportionalRowBurst,
  tierFor,
  tierGemMultiplier,
  tierHitsAll,
  tierRank,
  type GemValues,
  type MatchTier,
} from './effects';

/** Hard stop on cascade depth. A board that never settles is a bug, not a feature. */
const MAX_WAVES = 100;

export interface WaveReport {
  readonly wave: number;
  readonly multiplier: number;
  /** Match cells ∪ every cell of every full row ∪ the Aether flood. */
  readonly cleared: readonly CellIndex[];
  readonly runs: readonly MatchRun[];
  readonly rowsCleared: readonly number[];
  readonly counts: Readonly<Record<GemKind, number>>;
  /** Highest match tier reached this wave, for presentation. */
  readonly tier: MatchTier | null;
  /** Ember from a Surge/Aether run this wave — routes to every living enemy. */
  readonly emberHitsAll: boolean;
  /** Venom from a Surge/Aether run this wave. */
  readonly venomHitsAll: boolean;
  readonly emberDamage: number;
  readonly frostBlock: number;
  readonly poison: number;
  readonly voltUnits: number;
  readonly energyGained: number;
  readonly rowBurst: number;
  /** Stone gems destroyed by this wave's row clears. Mason's Trowel keys off it. */
  readonly stonesInRows: number;
}

export interface ResolveReport {
  readonly waves: readonly WaveReport[];
  readonly totalEmberDamage: number;
  readonly totalFrostBlock: number;
  readonly totalPoison: number;
  readonly energyGained: number;
  readonly voltRemainder: number;
  readonly rowBurst: number;
  /** emberDamage + rowBurst — what lands on the current target. */
  readonly totalDamage: number;
  readonly totalGemsCleared: number;
}

function emptyCounts(): Record<GemKind, number> {
  return { ember: 0, frost: 0, venom: 0, volt: 0, stone: 0 };
}

/**
 * Cells an Aether run drags in with it: every gem of that color anywhere on
 * the board, not just the run. Computed against the pre-clear grid so the
 * flood cannot miss a gem that would fall into range later.
 */
function aetherFlood(grid: Grid, runs: readonly MatchRun[], into: Set<CellIndex>): void {
  const kinds = new Set<GemKind>();
  for (const run of runs) {
    if (tierFor(run.cells.length) === 'aether') kinds.add(run.kind);
  }
  if (kinds.size === 0) return;
  for (let i = 0; i < grid.length; i++) {
    const cell = grid[i] ?? null;
    if (cell !== null && kinds.has(cell)) into.add(i);
  }
}

/**
 * Resolve the well to rest.
 *
 * Per wave: find runs and full rows, union their cells, add any Aether flood,
 * fire every cleared gem once, clear, drop, repeat.
 *
 * Match size changes the kind of payoff, not just the volume — see MatchTier
 * in effects.ts. A cell cleared by both a match and a full row fires ONCE;
 * a cell belonging to two runs (an L corner) takes the higher tier once.
 *
 * Mutates `grid` in place. Returns everything that happened; this module has
 * no notion of HP, Block, or a target enemy.
 */
export function resolveWell(
  grid: Grid,
  values: GemValues,
  voltRemainder = 0,
  cascadeCap: number = CASCADE_CAP,
): ResolveReport {
  const waves: WaveReport[] = [];
  let totalEmber = 0;
  let totalFrost = 0;
  let totalPoison = 0;
  let totalEnergy = 0;
  let totalBurst = 0;
  let totalCleared = 0;
  let volt = voltRemainder;

  for (let wave = 1; wave <= MAX_WAVES; wave++) {
    const runs = findRuns(grid);
    const rows = findFullRows(grid);
    if (runs.length === 0 && rows.length === 0) break;
    if (wave === MAX_WAVES) {
      throw new Error(`resolveWell: exceeded ${MAX_WAVES} waves without settling`);
    }

    const cleared = new Set<CellIndex>();
    for (const run of runs) for (const c of run.cells) cleared.add(c);
    for (const r of rows) for (let c = 0; c < WELL_COLS; c++) cleared.add(idx(r, c));
    aetherFlood(grid, runs, cleared);

    // Each cell carries the multiplier of the best run it belongs to. Cells
    // cleared only by a row or by an Aether flood fire at base.
    const cellMult = new Map<CellIndex, number>();
    let highest: MatchTier | null = null;
    let emberHitsAll = false;
    let venomHitsAll = false;
    for (const run of runs) {
      const tier = tierFor(run.cells.length);
      const m = tierGemMultiplier(tier);
      for (const c of run.cells) {
        cellMult.set(c, Math.max(cellMult.get(c) ?? 0, m));
      }
      if (highest === null || tierRank(tier) > tierRank(highest)) highest = tier;
      if (tierHitsAll(tier)) {
        if (run.kind === 'ember') emberHitsAll = true;
        if (run.kind === 'venom') venomHitsAll = true;
      }
    }

    const counts = emptyCounts();
    const weighted = emptyCounts();
    for (const c of cleared) {
      const cell = grid[c] ?? null;
      if (cell === null) continue;
      counts[cell]++;
      weighted[cell] += cellMult.get(c) ?? 1;
    }

    // Chain Amulet raises the cap; the ladder still never steps down.
    const mult = Math.min(Math.max(wave, 1), cascadeCap);

    const emberDamage = weighted.ember * values.emberDamage * mult;
    const frostBlock = weighted.frost * values.frostBlock * mult;
    const poison = weighted.venom * values.venomPoison * mult;

    // Volt is a meter, not a per-wave grant: the remainder is per-combat state
    // the caller threads back in. That persistence is what makes Volt Conduit
    // worth a card slot rather than a rounding error.
    const voltUnits = weighted.volt * mult;
    volt += voltUnits;
    const energyGained = Math.floor(volt / values.voltPerEnergy);
    totalEnergy += energyGained;
    volt -= energyGained * values.voltPerEnergy;

    // Burst is taxed by how much of the cleared row was real gems — a solid
    // Stone row must still leave the well, but it is not damage.
    let realInRows = 0;
    let stonesInRows = 0;
    for (const r of rows) {
      for (let c = 0; c < WELL_COLS; c++) {
        const cell = grid[idx(r, c)] ?? null;
        if (cell === null) continue;
        if (cell === 'stone') stonesInRows++;
        else realInRows++;
      }
    }
    const rowBurst = proportionalRowBurst(rows.length, realInRows, WELL_COLS);
    totalBurst += rowBurst;
    totalEmber += emberDamage;
    totalFrost += frostBlock;
    totalPoison += poison;
    totalCleared += cleared.size;

    waves.push({
      wave,
      multiplier: mult,
      cleared: [...cleared],
      runs,
      rowsCleared: rows,
      counts,
      tier: highest,
      emberHitsAll,
      venomHitsAll,
      emberDamage,
      frostBlock,
      poison,
      voltUnits,
      energyGained,
      rowBurst,
      stonesInRows,
    });

    clearCells(grid, cleared);
    applyGravity(grid);
  }

  return {
    waves,
    totalEmberDamage: totalEmber,
    totalFrostBlock: totalFrost,
    totalPoison: totalPoison,
    energyGained: totalEnergy,
    voltRemainder: volt,
    rowBurst: totalBurst,
    totalDamage: totalEmber + totalBurst,
    totalGemsCleared: totalCleared,
  };
}

export { idxCol, idxRow };
